import { apiUrl } from "../api/endpoints";
import React, {useEffect, useState, useContext, useRef, useCallback} from "react";
import Spinner from '../Components/Spinner';
import axios from 'axios';
import { countries } from '../lib/countries';
import { useAuth } from "../hooks/AuthProvider";
import moment from 'moment';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/ModeOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { deleteSubscriber, deleteMultiSubscribers } from '../lib/actions';
import DataGridTable from "../Components/DataGridTable";
import { GridActionsCellItem } from '@mui/x-data-grid';
import './edit.css';
import '../Components/modal.css';

import { LanguageContext } from '../context/LanguageContext';
import subscriptionTranslations from '../languages/subscription.json';

import AppDialog from '../Components/AppDialog';
import { getErrorMessage } from '../utils/errorUtils';
import { AddNewIcon, NameIcon } from '../svg/UtilsIcons';
import { EmailIcon } from "../svg/UserIcon";
import { MaxDatastreamsIcon, MaxDevicesIcon, MaxTemplatesIcon, MaxUsersIcon } from "../svg/SubscriptionIcon";
import { useLocation, useNavigate } from "react-router-dom";

const URL = apiUrl('subscriber');
const defaultCountry =
    countries.find((c) => c.name === 'United States') || { name: 'United States', code: '+1', position: '' };

const getCountryFromPhoneInput = (phoneValue, preferredCountryName) => {
    const normalizedPhone = String(phoneValue || '').replace(/[^\d+]/g, '');

    if (!normalizedPhone.startsWith('+')) {
        return null;
    }

    const matches = countries.filter((country) => normalizedPhone.startsWith(country.code));
    if (matches.length === 0) {
        return null;
    }

    const sortedMatches = [...matches].sort((a, b) => b.code.length - a.code.length);
    const preferredMatch = sortedMatches.find((country) => country.name === preferredCountryName);
    if (preferredMatch) {
        return preferredMatch;
    }

    const defaultMatch = sortedMatches.find((country) => country.name === defaultCountry.name);
    return defaultMatch || sortedMatches[0];
};

const getCountryFlagStyle = (country) => {
    if (country?.flagImage) {
        return {
            backgroundImage: `url(${country.flagImage})`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
        };
    }

    return {
        backgroundPosition: country?.position || '0px 0px',
    };
};



const PhoneIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M6 2H10C10.5523 2 11 2.44772 11 3V13C11 13.5523 10.5523 14 10 14H6C5.44772 14 5 13.5523 5 13V3C5 2.44772 5.44772 2 6 2Z"
            stroke="#00C0C7"
            strokeWidth="1.2"
        />
        <path
            d="M7 3.5H9"
            stroke="#00C0C7"
            strokeWidth="1.2"
            strokeLinecap="round"
        />
        <circle cx="8" cy="11.5" r="0.6" fill="#00C0C7" />
    </svg>
);

const LimitsIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <rect
            x="2"
            y="2"
            width="12"
            height="12"
            rx="2"
            stroke="#00C0C7"
            strokeWidth="1.2"
        />
        <path
            d="M5 5H11"
            stroke="#00C0C7"
            strokeWidth="1.2"
            strokeLinecap="round"
        />
        <path
            d="M5 8H9.5"
            stroke="#00C0C7"
            strokeWidth="1.2"
            strokeLinecap="round"
        />
        <path
            d="M5 11H8"
            stroke="#00C0C7"
            strokeWidth="1.2"
            strokeLinecap="round"
        />
    </svg>
);

const AddSubscription = ({ close, refresh, showAlert, canAdd }) => {
    const { lang } = useContext(LanguageContext);
    const t = subscriptionTranslations[lang].addSubscription;
    const isRtl = ['ar', 'ur'].includes(lang);

    const [subscriber, setSubscriber] = useState({
        name: '',
        email: '',
        phone: '',
        application: '',
        password: '',
        max_devices: 10,
        max_templates: 10,
        max_datastream_per_template: 20,
        max_users: 1
    });

    const createSubscription = () => {
        if (!canAdd) {
            showAlert('error', t.permissionMessage || 'You do not have permission to add subscriptions.');
            return;
        }
        axios.post(URL, subscriber, { headers: { "Content-Type": "application/json" } })
            .then(res => {
                if (res.status === 201 && res.data.status === 'OK') {
                    const ownerEmail = res.data?.data?.owner_user?.email || subscriber.email;
                    const hasPassword = Boolean(String(subscriber.password || '').trim());
                    const inviteFailures = res.data?.data?.invitationEmailFailures || [];
                    let message = hasPassword
                        ? (t.ownerPasswordSetMessage?.replace('{email}', ownerEmail) || `Subscriber created. Owner ${ownerEmail} can log in with the password you set.`)
                        : (t.ownerInitializePasswordMessage?.replace('{email}', ownerEmail) || `Subscriber created. Owner ${ownerEmail} received a password setup email.`);
                    if (inviteFailures.length > 0) {
                        message = t.ownerInviteFailedMessage?.replace('{email}', ownerEmail) ||
                            `Subscriber created, but the password setup email was not sent to ${ownerEmail}.`;
                    }
                    showAlert(inviteFailures.length > 0 ? 'warning' : 'success', message);
                    refresh();
                    close();
                }
            })
            .catch(err => {
                console.error("Create Subscriber Error:", err);
                showAlert('error', getErrorMessage(err));
            });
    };

    if (!canAdd) {
        return (
            <div
                id="modal"
                style={{
                    direction: isRtl ? 'rtl' : 'ltr'
                }}
            >
                <div
                    id="popup"
                    className="dashboard-modal"
                    style={{
                        animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
                        fontSize:
                            'calc(var(--body-font-size) * var(--font-size-multiplier))'
                    }}
                >
                    <div className="dashboard-header">
                        <div className="dashboard-header-icon">
                            <AddNewIcon />
                        </div>
                        <div className="dashboard-header-text">
                            <h2>{t.permissionTitle || 'Permission Denied'}</h2>
                            <p>
                                {t.permissionMessage ||
                                    'You do not have permission to add new subscriptions.'}
                            </p>
                        </div>
                    </div>

                    <div className="modal-divider" />

                    <div className="modal-content dashboard-content" />

                    <div className="modal-footer dashboard-footer">
                        <button
                            className="btn btn-cancel"
                            onClick={close}
                        >
                            {t.buttonClose || subscriptionTranslations[lang].buttonCancel || 'Close'}
                        </button>
                        <div />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <Subscription
            close={close}
            title={t.title}
            subtitle={t.subtitle || t.description || ''}
            submitLabel={t.submitLabel}
            subscriber={subscriber}
            setSubscriber={setSubscriber}
            onSubmit={createSubscription}
            view={false}
            loading={false}
        />
    );
};


const EditSubscription = ({ close, refresh, id, view, showAlert, canEdit }) => {
    const { lang } = useContext(LanguageContext);
    const t = subscriptionTranslations[lang].editSubscription;
    const [subscriber, setSubscriber] = useState({
        id: id,
        name: '',
        email: '',
        phone: '',
        application: '',
        max_devices: 10,
        max_templates: 10,
        max_datastream_per_template: 20,
        max_users: 1
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (id) {
            setLoading(true);
            axios.get(`${URL}/${id}`)
                .then(res => {
                    if (res.data && res.data.status === 'OK') {
                        setSubscriber(res.data.data);
                    } else {
                        showAlert('error', 'Received an invalid response from the server.');
                    }
                })
                .catch(err => {
                    console.error("Fetch Subscriber Error:", err);
                    showAlert('error', getErrorMessage(err));
                    setTimeout(close, 2000);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [id, close, showAlert]);

    const updateSubscription = () => {
        if (!canEdit) {
            showAlert('error', t.permissionMessage || 'You do not have permission to edit subscriptions.');
            return;
        }
        const subscriberWithId = { ...subscriber, id: id };
        axios.put(URL, subscriberWithId, { headers: { "Content-Type": "application/json" } })
            .then(res => {
                if (res.data && res.data.status === 'OK') {
                    showAlert('success', res.data.message || t.successMessage || 'Subscriber updated successfully!');
                    refresh();
                    close();
                }
            })
            .catch(err => {
                console.error("Update Subscriber Error:", err);
                showAlert('error', getErrorMessage(err));
            });
    };

    const isViewMode = view || !canEdit;
    const titleText = isViewMode ? (t.viewTitle || t.title) : t.title;
    const submitLabelText = t.submitLabel;

    return (
        <Subscription
            close={close}
            view={isViewMode}
            title={titleText}
            subtitle={t.subtitle || t.description || ''}
            submitLabel={submitLabelText}
            subscriber={subscriber}
            setSubscriber={setSubscriber}
            onSubmit={updateSubscription}
            loading={loading}
        />
    );
};


const Subscription = ({
    close,
    onSubmit,
    subscriber,
    setSubscriber,
    title,
    subtitle,
    submitLabel,
    view,
    loading
}) => {
    const { lang } = useContext(LanguageContext);
    const tAdd = subscriptionTranslations[lang].addSubscription;
    const tFields = tAdd.fields;
    const isRtl = ['ar', 'ur'].includes(lang);
    const [isCountryOpen, setIsCountryOpen] = useState(false);
    const countryMenuRef = useRef(null);

    const onChange = (e) =>
        setSubscriber(prev => ({
            ...prev,
            [e.target.name]: e.target.type === 'number'
                ? (e.target.value === '' ? '' : Number(e.target.value))
                : e.target.value
        }));

    const handlePhoneChange = useCallback((phoneValue) => {
        setSubscriber((prev) => ({
            ...prev,
            phone: phoneValue,
        }));
    }, [setSubscriber]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!isCountryOpen) return;
            if (countryMenuRef.current && !countryMenuRef.current.contains(event.target)) {
                setIsCountryOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isCountryOpen]);

   
    const selectCountry = () => {
        setIsCountryOpen((prev) => !prev);
    };

    const selectCountryItem = (e, country) => {
        e.stopPropagation();
        if (!country?.code) return;
        setSubscriber(prev => ({
            ...prev,
            phone: country.code,
        }));
        setIsCountryOpen(false);
    };
    const currentCountry = getCountryFromPhoneInput(subscriber.phone) || defaultCountry;

    const registeredLabel =
        subscriptionTranslations[lang].columns?.registered ||
        'Registered at';

    return (
        <div
            id="modal"
            style={{
                direction: isRtl ? 'rtl' : 'ltr'
            }}
        >
            <div
                id="popup"
                className="dashboard-modal"
                style={{
                    animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
                    fontSize:
                        'calc(var(--body-font-size) * var(--font-size-multiplier))'
                }}
                onClick={e => e.stopPropagation()}
            >
                <div className="dashboard-header">
                    <div className="dashboard-header-icon">
                        <AddNewIcon />
                    </div>
                    <div className="dashboard-header-text">
                        <h2>{title}</h2>
                        {subtitle ? <p>{subtitle}</p> : null}
                    </div>
                </div>

                <div className="modal-divider" />

                {loading ? (
                    <div
                        className="modal-content dashboard-content"
                        style={{ minHeight: 200, display: 'flex', alignItems: 'center' }}
                    >
                        <Spinner />
                    </div>
                ) : (
                    <div className="modal-content dashboard-content">
                        <div className="form-section">
                            <div className="form-section-title">
                                <span className="form-section-icon">
                                    <NameIcon />
                                </span>
                                <span className="form-section-title-text">
                                    {tFields.name}
                                </span>
                            </div>

                            <div className="form-item">
                                {view ? (
                                    <div
                                        className="add-column"
                                        style={{
                                            padding: '6px 12px',
                                            backgroundColor: 'var(--header-hover)',
                                            border: '1px solid var(--modal-border)',
                                            opacity: 0.9
                                        }}
                                    >
                                        {subscriber.name}
                                    </div>
                                ) : (
                                    <div className="field-with-counter">
                                        <input
                                            type="text"
                                            className="add-column"
                                            name="name"
                                            placeholder={tFields.name}
                                            maxLength={50}
                                            value={subscriber.name ?? ''}
                                            onChange={onChange}
                                        />
                                        <span className="field-counter">
                                            {(subscriber.name?.length || 0)} / 50
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="form-section">
                            <div className="form-section-title">
                                <span className="form-section-icon">
                                    <EmailIcon />
                                </span>
                                <span className="form-section-title-text">
                                    {tFields.email}
                                </span>
                            </div>

                            <div className="form-item">
                                {view ? (
                                    <div
                                        className="add-column"
                                        style={{
                                            padding: '6px 12px',
                                            backgroundColor: 'var(--header-hover)',
                                            border: '1px solid var(--modal-border)',
                                            opacity: 0.9
                                        }}
                                    >
                                        {subscriber.email}
                                    </div>
                                ) : (
                                    <input
                                        type="email"
                                        name="email"
                                        className="add-column"
                                        placeholder={tFields.email}
                                        value={subscriber.email ?? ''}
                                        onChange={onChange}
                                    />
                                )}
                            </div>
                        </div>

                        {!view ? (
                            <div className="form-section">
                                <div className="form-section-title">
                                    <span className="form-section-icon">
                                        <LockOutlinedIcon fontSize="small" />
                                    </span>
                                    <span className="form-section-title-text">
                                        {tFields.password || 'Owner Password'}
                                    </span>
                                    <span className="subscription-optional-pill">
                                        {tFields.passwordOptional || 'Optional'}
                                    </span>
                                </div>

                                <div className="form-item">
                                    <input
                                        type="password"
                                        className="add-column"
                                        name="password"
                                        placeholder={tFields.passwordPlaceholder || 'Leave blank to send setup email'}
                                        maxLength={128}
                                        autoComplete="new-password"
                                        value={subscriber.password ?? ''}
                                        onChange={onChange}
                                    />
                                    <span className="form-helper subscription-password-helper">
                                        {tFields.passwordHelper || 'Set the owner password now, or leave it empty to send the same secure setup email used when adding users.'}
                                    </span>
                                </div>
                            </div>
                        ) : null}

                        <div className="form-section">

                            <div className="form-item">
                                <div className="form-section-title">
                                <span className="form-section-icon">
                                    <MaxDevicesIcon />
                                </span>
                                <label className="form-lbl">{tFields.max_devices}</label>
                                </div>
                                {view ? (
                                    <div
                                        className="add-column"
                                        style={{
                                            padding: '6px 12px',
                                            backgroundColor: 'var(--header-hover)',
                                            border: '1px solid var(--modal-border)',
                                            opacity: 0.9
                                        }}
                                    >
                                        {subscriber.max_devices}
                                    </div>
                                ) : (
                                    <input
                                        type="number"
                                        className="add-column"
                                        name="max_devices"
                                        placeholder={tFields.max_devices}
                                        value={subscriber.max_devices ?? 0}
                                        onChange={onChange}
                                    />
                                )}
                            </div>

                            <div className="form-item">
                                <div className="form-section-title">
                                <span className="form-section-icon">
                                    <MaxTemplatesIcon />
                                </span>
                                <label className="form-lbl">{tFields.max_templates}</label>
                                </div>
                                {view ? (
                                    <div
                                        className="add-column"
                                        style={{
                                            padding: '6px 12px',
                                            backgroundColor: 'var(--header-hover)',
                                            border: '1px solid var(--modal-border)',
                                            opacity: 0.9
                                        }}
                                    >
                                        {subscriber.max_templates}
                                    </div>
                                ) : (
                                    <input
                                        type="number"
                                        className="add-column"
                                        name="max_templates"
                                        placeholder={tFields.max_templates}
                                        value={subscriber.max_templates ?? 0}
                                        onChange={onChange}
                                    />
                                )}
                            </div>

                            <div className="form-item">
                                <div className="form-section-title">
                                <span className="form-section-icon">
                                    <MaxDatastreamsIcon />
                                </span>
                                <label className="form-lbl">{tFields.max_datastream_per_template}</label>
                                </div>
                                {view ? (
                                    <div
                                        className="add-column"
                                        style={{
                                            padding: '6px 12px',
                                            backgroundColor: 'var(--header-hover)',
                                            border: '1px solid var(--modal-border)',
                                            opacity: 0.9
                                        }}
                                    >
                                        {subscriber.max_datastream_per_template}
                                    </div>
                                ) : (
                                    <input
                                        type="number"
                                        className="add-column"
                                        name="max_datastream_per_template"
                                        placeholder={tFields.max_datastream_per_template}
                                        value={subscriber.max_datastream_per_template ?? 0}
                                        onChange={onChange}
                                    />
                                )}
                            </div>

                            <div className="form-item">
                                <div className="form-section-title">
                                <span className="form-section-icon">
                                    <MaxUsersIcon />
                                </span>
                                <label className="form-lbl">{tFields.max_users}</label>
                                </div>
                                {view ? (
                                    <div
                                        className="add-column"
                                        style={{
                                            padding: '6px 12px',
                                            backgroundColor: 'var(--header-hover)',
                                            border: '1px solid var(--modal-border)',
                                            opacity: 0.9
                                        }}
                                    >
                                        {subscriber.max_users}
                                    </div>
                                ) : (
                                    <input
                                        type="number"
                                        className="add-column"
                                        name="max_users"
                                        placeholder={tFields.max_users}
                                        value={subscriber.max_users ?? 0}
                                        onChange={onChange}
                                    />
                                )}
                            </div>
                        </div>

                        <div className="form-section">
                            <div className="form-section-title">
                                <span className="form-section-icon">
                                    <PhoneIcon />
                                </span>
                                <span className="form-section-title-text">
                                    {tFields.phone}
                                </span>
                            </div>

                            <div className="form-item">
                                <div style={{ position: 'relative' }}>
                                    {view ? (
                                        <div
                                            className="add-column"
                                            style={{
                                                padding: '6px 12px 6px 64px',
                                                backgroundColor: 'var(--header-hover)',
                                                border: '1px solid var(--modal-border)',
                                                opacity: 0.9
                                            }}
                                        >
                                            {subscriber.phone}
                                        </div>
                                    ) : (
                                        <input
                                            type="text"
                                            name="phone"
                                            className="add-column"
                                            style={{ paddingInlineStart: '64px' }}
                                            placeholder={tFields.phone}
                                            value={subscriber.phone || ''}
                                            onChange={(e) => handlePhoneChange(e.target.value)}
                                        />
                                    )}

                                    {!view && (
                                        <div
                                            className="edit-select-country"
                                            title={tAdd.selectCountryTitle || tFields.phone}
                                            ref={countryMenuRef}
                                            onClick={selectCountry}
                                        >
                                            <div
                                                style={getCountryFlagStyle(currentCountry)}
                                                className="edit-country-flag"
                                            ></div>
                                            <span className={`edit-arrow ${isCountryOpen ? 'selected' : ''}`}></span>
                                            <ul
                                                className={`edit-country-list ${isCountryOpen ? 'selected' : ''}`}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {countries.map((country, idx) => (
                                                    <li
                                                        className="edit-country-list-item"
                                                        key={`${country.name}-${country.code}-${idx}`}
                                                        onClick={(e) => selectCountryItem(e, country)}
                                                    >
                                                        <span style={getCountryFlagStyle(country)} className="edit-country-flag"></span>
                                                        <span className="edit-country-name">
                                                            {country.name}
                                                        </span>
                                                        <span className="edit-country-code">
                                                            {country.code}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="form-section">
                            <div className="form-section-title">
                                <span className="form-section-icon">
                                    <LimitsIcon />
                                </span>
                                <span className="form-section-title-text">
                                    {tFields.application || 'Application'}
                                </span>
                            </div>

                            <div className="form-item">
                                {view ? (
                                    <div
                                        className="add-column"
                                        style={{
                                            padding: '6px 12px',
                                            backgroundColor: 'var(--header-hover)',
                                            border: '1px solid var(--modal-border)',
                                            opacity: 0.9,
                                            whiteSpace: 'pre-wrap'
                                        }}
                                    >
                                        {subscriber.application || '-'}
                                    </div>
                                ) : (
                                    <textarea
                                        className="add-column"
                                        name="application"
                                        rows={3}
                                        maxLength={4000}
                                        placeholder={tFields.application || 'Application'}
                                        value={subscriber.application ?? ''}
                                        onChange={onChange}
                                        style={{ resize: 'vertical' }}
                                    />
                                )}
                            </div>
                        </div>

                        {subscriber.registered ? (
                            <div className="form-section">
                                <div className="form-section-title">
                                    <span className="form-section-icon">
                                        <LimitsIcon />
                                    </span>
                                    <span className="form-section-title-text">
                                        {registeredLabel}
                                    </span>
                                </div>

                                <div className="form-item">
                                    <div
                                        className="add-column"
                                        style={{
                                            padding: '6px 12px',
                                            backgroundColor: 'var(--header-hover)',
                                            border: '1px solid var(--modal-border)',
                                            opacity: 0.9
                                        }}
                                    >
                                        {subscriber.registered}
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>
                )}

                <div className="modal-footer dashboard-footer">
                    <button
                        className="btn btn-cancel"
                        onClick={close}
                    >
                        {subscriptionTranslations[lang].buttonCancel || 'Cancel'}
                    </button>
                    {!view && (
                        <button
                            className="btn btn-apply dashboard-primary-btn"
                            onClick={onSubmit}
                        >
                            {submitLabel}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export const Subscriptions = () => {
    const { lang } = useContext(LanguageContext);
    const t = subscriptionTranslations[lang];
    const { permissions, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const canView = permissions['view_subscriptions'];
    const canAdd = permissions['create_subscription'];
    const canEdit = permissions['edit_subscription'];
    const canDelete = permissions['delete_subscription'];

    const [rows, setrows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openAddModal, setOpenAddModal] = useState(false);
    const [editModal, setEditModal] = useState({ open: false, id: 0, view: true });

    const pagingoptions = [10, 20, 50, 100];
const isRtl = ['ar', 'ur'].includes(lang);

const resolverRef = useRef(null);
const [dialog, setDialog] = useState({
    open: false,
    type: "alert", // "alert" | "confirm"
    variant: "info", // "info" | "danger" | "error"
    icon: "alert", // "alert" | "delete"
    title: "",
    message: "",
    inputLabel: "",
    inputPlaceholder: "",
    inputType: "text",
    inputValue: "",
    confirmText: "Ok",
    cancelText: "Cancel",
    showCancel: false,
});

const closeDialog = useCallback((result = false) => {
    setDialog((d) => ({ ...d, open: false }));
    if (resolverRef.current) {
        resolverRef.current(result);
        resolverRef.current = null;
    }
}, []);

const alert = useCallback(
    (opts) =>
        new Promise((resolve) => {
            resolverRef.current = resolve;

            setDialog({
                open: true,
                type: opts?.type || "alert",
                variant: opts?.variant || "info",
                icon: opts?.icon || "alert",
                title: opts?.title || "",
                message: opts?.message || "",
                inputLabel: opts?.inputLabel || "",
                inputPlaceholder: opts?.inputPlaceholder || "",
                inputType: opts?.inputType || "text",
                inputValue: opts?.inputValue || "",
                confirmText: opts?.confirmText || "Ok",
                cancelText: opts?.cancelText || "Cancel",
                showCancel: Boolean(opts?.showCancel),
            });
        }),
    []
);

const showAlert = useCallback(
    async (type, message) => {
        const variant =
            type === "error" ? "error" : type === "warning" ? "info" : type === "success" ? "success" : "info";

        await alert({
            type: "alert",
            variant,
            icon: "alert",
            title:
                type === "error"
                    ? (t?.alert?.errorTitle || "Error")
                    : type === "warning"
                        ? (t?.alert?.warningTitle || "Warning")
                        : type === "success"
                            ? (t?.alert?.successTitle || "Success")
                            : (t?.alert?.infoTitle || "Info"),
            message: message || "",
            confirmText: "Ok",
        });
    },
    [alert, t?.alert?.errorTitle, t?.alert?.warningTitle, t?.alert?.successTitle, t?.alert?.infoTitle]
);const baseColumns = [
        { field: 'id', headerName: t.columns.id, width: 200, align: 'center', headerAlign: 'center' },
        { field: 'name', headerName: t.columns.name, width: 200, align: 'left', headerAlign: 'left' },
        { field: 'email', headerName: t.columns.email, width: 200, align: 'center', headerAlign: 'center' },
        { field: 'phone', headerName: t.columns.phone, width: 200, align: 'center', headerAlign: 'center' },
        { field: 'application', headerName: t.columns.application || 'Application', width: 260, align: 'left', headerAlign: 'left' },
        { field: 'max_devices', headerName: t.columns.max_devices, width: 200, type: 'number', align: 'center', headerAlign: 'center' },
        { field: 'max_templates', headerName: t.columns.max_templates, width: 200, type: 'number', align: 'center', headerAlign: 'center' },
        { field: 'max_datastream_per_template', headerName: t.columns.max_datastream_per_template, width: 200, type: 'number', align: 'center', headerAlign: 'center' },
        { field: 'max_users', headerName: t.columns.max_users, width: 200, type: 'number', align: 'center', headerAlign: 'center' },
        {
            field: 'registered', headerName: t.columns.registered, width: 200, align: 'center', headerAlign: 'center', type: 'dateTime',
            valueGetter: (params) => params ? new Date(params) : null,
            valueFormatter: (params) => moment(params).format('DD/MM/YYYY HH:mm:ss')
        },
        {
            field: 'expired_at',
            headerName: t.columns.plan_expired_at || 'Plan Expired At',
            width: 180,
            align: 'center',
            headerAlign: 'center',
            type: 'date',
            valueGetter: (params) => params ? new Date(`${params}T00:00:00`) : null,
            valueFormatter: (params) => params ? moment(params).format('DD/MM/YYYY') : '-'
        },
    ];

    const columns = (canEdit || canDelete)
        ? [
            {
                field: 'actions', headerName: t.columns.actions, type: 'actions', width: 100,
                getActions: (params) => {
                    const actions = [];
                    if (canEdit) {
                        actions.push(
                            <GridActionsCellItem
                                key="edit"
                                icon={<EditIcon />}
                                showInMenu
                                label={t.edit}
                                onClick={() =>
                                    navigate(`/administration/subscriptions/details/${params.row.id}`, {
                                        state: { autoEdit: true },
                                    })
                                }
                            />
                        );
                    }
                    if (canDelete) {
                        actions.push(
                            <GridActionsCellItem
                                key="delete"
                                icon={<DeleteIcon style={{ color: 'red' }} />}
                                showInMenu
                                label={t.delete}
                                onClick={() => deleteSubscriber(params.row.id, fetchSubscriptions, alert)}
                            />
                        );
                    }
                    return actions;
                },
            },
            ...baseColumns,
        ]
        : baseColumns;


    const fetchSubscriptions = useCallback(() => {
        if (!canView) { setLoading(false); setrows([]); return; }
        setLoading(true);
        axios.get(URL)
            .then(result => {
                if (result.data && result.data.status === 'OK') {
                    setrows(Array.isArray(result.data.data) ? result.data.data : []);
                } else {
                    showAlert('error', 'Received an invalid response from the server.');
                    setrows([]);
                }
            })
            .catch(err => {
                console.error("Fetch Subscriptions Error:", err);
                const errorMessage = getErrorMessage(err);
                showAlert('error', errorMessage);
                setrows([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [canView, showAlert]);

    useEffect(() => {
        fetchSubscriptions();
    }, [fetchSubscriptions]);

    const editSubscriptionIdFromState = Number(location?.state?.editSubscriptionId);
    useEffect(() => {
        if (!Number.isInteger(editSubscriptionIdFromState) || editSubscriptionIdFromState <= 0) return;

        setEditModal({ open: true, id: editSubscriptionIdFromState, view: false });
        navigate(location.pathname, { replace: true, state: {} });
    }, [editSubscriptionIdFromState, location.pathname, navigate]);

    const handleRowClick = (params) => {
        navigate(`/administration/subscriptions/details/${params.row.id}`);
    };
    const handleMultiDelete = (ids) => {
        if (!canDelete) {
            showAlert('error', 'You do not have permission to delete subscriptions.');
            return;
        }
        deleteMultiSubscribers(ids, fetchSubscriptions, alert);
        setSelectedRows([]);
    };

    const [selectedRows, setSelectedRows] = React.useState([]);
    const selectActions = canDelete
        ? [{
            title: t.delete,
            icon: <DeleteIcon style={{ color: 'rgb(236, 69, 78)', marginInlineEnd: '20px' }} />,
            action: (ids) => { handleMultiDelete(ids); }
        }]
        : [];

    if (!canView) {
        return <div>{t.noPermission}</div>;
    }

    return ((user.role === 'Super' && canView)
        ? <div className="configuration devices-page" style={{ display: "flex", flexDirection: "column", height: "100%" }} dir={isRtl ? "rtl" : "ltr"}>
            <AppDialog
    open={dialog.open}
    type={dialog.type}
    variant={dialog.variant}
    icon={dialog.icon}
    title={dialog.title}
    message={dialog.message}
    confirmText={dialog.confirmText}
    cancelText={dialog.cancelText}
    showCancel={dialog.showCancel}
    inputLabel={dialog.inputLabel}
    inputPlaceholder={dialog.inputPlaceholder}
    inputType={dialog.inputType}
    inputValue={dialog.inputValue}
    onConfirm={() => closeDialog(dialog.type === "prompt" ? dialog.inputValue : true)}
    onCancel={() => closeDialog(false)}
    onClose={() => closeDialog(false)}
    onInputChange={(value) => setDialog((current) => ({ ...current, inputValue: value }))}
/>
            {openAddModal &&
                <AddSubscription
                    close={() => setOpenAddModal(false)}
                    refresh={fetchSubscriptions}
                    showAlert={showAlert}
                    canAdd={canAdd}
                />}
            {editModal.open &&
                <EditSubscription
                    close={() => setEditModal(prev => ({ ...prev, open: false }))}
                    refresh={fetchSubscriptions}
                    id={editModal.id}
                    view={editModal.view}
                    showAlert={showAlert}
                    canEdit={canEdit}
                />}

            <div className="device-main">
                <div className="devices-header">
                    <div className="devices-header-left">
                        <h2 className="devices-title" >{t.subscriptionsPageTitle}</h2>
                    </div>
                    {canAdd && (
                        <button className="devices-primary-btn" onClick={() => setOpenAddModal(true)}>
                            <span>{t.newSubscription}</span>
                        </button>
                    )}
                </div>

            {loading ?
                <Spinner />
                : rows.length > 0 ?
                    <div className="table-wrapper" style={{ flex: 1, minHeight: 0, marginTop: '20px' }}>
                        <DataGridTable
                            columns={columns}
                            rows={rows}
                            handleRowClick={handleRowClick}
                            idcolumn={"id"}
                            pagingoptions={pagingoptions}
                            checkboxSelection={true}
                            rowSelectionModel={selectedRows}
                            setRowSelectionModel={setSelectedRows}
                            selectActions={selectActions}
                            showToolbar={true}
                            tableKey='subscriptions'
                        />
                    </div>
                    : <div style={{
                        width: '100%',
                        flex: 1,
                        display: "flex",
                        alignItems: "center", justifyContent: "center"
                    }}>{t.noData}</div>}
                                </div>
        </div>
        : <div>{t.noPermission}</div>
    );
};
